/**
 * `DocumentPhotos` — foto de rosto + foto do documento de um usuário, nas telas de DETALHE.
 *
 * O bucket `users-documents` é PRIVADO: a URL crua devolve 403 e o <img> fica em branco sem erro
 * (foi exatamente esse o bug do visualizador do admin). A imagem vem SEMPRE da URL assinada de
 * `GET /api/v1/admin/users/{id}/document-url`, que expira em 15 min — por isso não fica em cache.
 *
 * Só no detalhe, nunca em listagem: mesma régua do CPF mascarado. Uma linha de tabela com 20
 * fotos de documento seria 20 presigns e um vazamento de dado pessoal em tela de varredura.
 */
import { useEffect, useState, type ReactElement } from 'react';
import { merchantApiFetch } from '../../api/fetcher';
import { Icon } from '../merchant';

export type DocumentKind = 'face' | 'identity';

async function fetchSignedUrl(userId: string, type: DocumentKind): Promise<string> {
  const path =
    `/api/v1/admin/users/${encodeURIComponent(userId)}/document-url?type=${encodeURIComponent(type)}`;
  const data = await merchantApiFetch<{ url?: string }>(path);
  if (!data.url) throw new Error('URL não disponível');
  return data.url;
}

function DocumentSlot({
  userId,
  type,
  label,
  present,
}: {
  userId: string;
  type: DocumentKind;
  label: string;
  present: boolean;
}): ReactElement {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!present) return undefined;
    setError(false);
    fetchSignedUrl(userId, type)
      .then((u) => { if (!cancelled) setUrl(u); })
      .catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, [userId, type, present]);

  return (
    <div>
      <div className="mb-1.5 text-[12px] font-bold uppercase tracking-[0.04em] text-[var(--m-fg-muted)]">
        {label}
      </div>
      <div className="flex h-44 w-full items-center justify-center overflow-hidden rounded-[var(--m-r-md)] border border-[var(--m-border)] bg-[var(--m-bg-alt)]">
        {!present ? (
          <span className="text-[13px] text-[var(--m-fg-subtle)]">Não enviado</span>
        ) : error ? (
          <span className="text-[13px] text-[var(--m-danger)]">Falha ao carregar</span>
        ) : url ? (
          <img src={url} alt={label} className="h-full w-full object-contain" onError={() => setError(true)} />
        ) : (
          <Icon name="image" size={20} />
        )}
      </div>
    </div>
  );
}

export function DocumentPhotos({
  userId,
  hasFacePhoto,
  hasIdentityPhoto,
}: {
  userId: string;
  hasFacePhoto: boolean;
  hasIdentityPhoto: boolean;
}): ReactElement {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <DocumentSlot userId={userId} type="face" label="Foto de rosto" present={hasFacePhoto} />
      <DocumentSlot userId={userId} type="identity" label="Documento (RG ou CNH)" present={hasIdentityPhoto} />
    </div>
  );
}
